import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, filter, map } from 'rxjs/operators';
import { InfoService } from '@wonder-core-ng/info-store/info.service';
import { selectUserId } from '@wonder-core-ng/session-store/session.selectors';
import { selectSelectedTenantId } from '@wonder-core-ng/tenant-store/tenant.selectors';
import { isNotNull } from '@wonder-core-ng/utils';
import * as DashboardActions from './dashboard.actions';
import { DashboardApiService } from './dashboard.api.service';
import { selectDashboards } from './dashboard.selectors';
import { IDashboard } from './interfaces/dashboard.interface';

@Injectable()
export class DashboardEffects {
  private actions$ = inject(Actions);
  private dashboardApi = inject(DashboardApiService);
  private store = inject(Store);
  private infoService = inject(InfoService);

  loadDashboard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.loadDashboard),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([{ dashboardId }, tenantId]) =>
        this.dashboardApi.loadDashboard(dashboardId, tenantId).pipe(
          map((dashboard) => DashboardActions.loadDashboardSuccess({ dashboard })),
          catchError((error: unknown) => {
            // Without access (403) or missing (404) the page explains it itself: no raw error toast (WB-185).
            const status = (error as { status?: number } | null)?.status;
            if (status !== 403 && status !== 404) {
              this.infoService.addMessage({
                message: '$dashboard.error-loading-dashboard',
                severity: 'error',
                error,
              });
            }
            return of(DashboardActions.loadDashboardFailure({ error }));
          })
        )
      )
    );
  });

  loadBlockActionExecutions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.loadBlockActionExecutions),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([{ blockId }, tenantId]) =>
        this.dashboardApi.loadBlockActionExecutions(blockId, tenantId).pipe(
          map((executions) =>
            DashboardActions.loadBlockActionExecutionsSuccess({ blockId, executions })
          ),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$action-list.load-error',
              severity: 'error',
              error,
            });
            return of(DashboardActions.loadBlockActionExecutionsFailure({ blockId, error }));
          })
        )
      )
    );
  });

  markActionExecutionRead$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.markActionExecutionRead),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([{ blockId, executionId }, tenantId]) =>
        this.dashboardApi.markActionExecutionRead(executionId, tenantId).pipe(
          map((execution) =>
            DashboardActions.markActionExecutionReadSuccess({ blockId, execution })
          ),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$action-list.mark-read-error',
              severity: 'error',
              error,
            });
            return of(
              DashboardActions.markActionExecutionReadFailure({ blockId, executionId, error })
            );
          })
        )
      )
    );
  });

  loadDashboards$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.loadDashboards),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([_, tenantId]) =>
        this.dashboardApi.loadDashboards(tenantId).pipe(
          map((list) =>
            DashboardActions.loadDashboardsSuccess({ data: { list, total: list.length } })
          ),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$dashboard.error-loading-dashboards',
              severity: 'error',
              error,
            });
            return of(DashboardActions.loadDashboardsFailure({ error }));
          })
        )
      )
    );
  });

  createDashboard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.createDashboard),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatLatestFrom(() => this.store.select(selectUserId).pipe(filter(isNotNull))),
      concatMap(([[{ dashboard }, tenantId], userId]) =>
        this.dashboardApi.createDashboard(dashboard, tenantId).pipe(
          map((created) => {
            this.infoService.addMessage({
              message: '$dashboard.success-creating-new-dashboard',
              severity: 'success',
            });
            const d: IDashboard = {
              ...created,
              author: created.author ?? userId.toString(),
              blocks: created.blocks ?? [],
            };
            return DashboardActions.createDashboardSuccess({ dashboard: d });
          }),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$dashboard.error-creating-new-dashboard',
              severity: 'error',
              error,
            });
            return of(DashboardActions.createDashboardFailure({ error }));
          })
        )
      )
    );
  });

  updateDashboard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.updateDashboard),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([{ dashboard }, tenantId]) => {
        return this.dashboardApi
          .updateDashboard(
            dashboard.id,
            {
              name: dashboard.name,
              icon: dashboard.icon,
              priority: dashboard.priority,
              header: dashboard.header,
            },
            tenantId
          )
          .pipe(
            map((updated) => {
              this.infoService.addMessage({
                message: '$dashboard.success-updating-dashboard',
                severity: 'success',
              });
              return DashboardActions.updateDashboardSuccess({
                dashboard: { ...dashboard, ...updated },
              });
            }),
            catchError((error: unknown) => {
              this.infoService.addMessage({
                message: '$dashboard.error-updating-dashboard',
                severity: 'error',
                error,
              });
              return of(DashboardActions.updateDashboardFailure({ error }));
            })
          );
      })
    );
  });

  deleteDashboard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.deleteDashboard),
      concatLatestFrom(() => this.store.select(selectSelectedTenantId).pipe(filter(isNotNull))),
      concatMap(([{ dashboardId }, tenantId]) =>
        this.dashboardApi.deleteDashboard(dashboardId, tenantId).pipe(
          map(() => {
            this.infoService.addMessage({
              message: '$dashboard.success-deleting-dashboard',
              severity: 'success',
            });
            return DashboardActions.deleteDashboardSuccess();
          }),
          catchError((error: unknown) => {
            this.infoService.addMessage({
              message: '$dashboard.error-deleting-dashboard',
              severity: 'error',
              error,
            });
            return of(DashboardActions.deleteDashboardFailure({ error }));
          })
        )
      )
    );
  });

  sortDashboardBlocks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.sortDashboardBlocks),
      concatLatestFrom(() => this.store.select(selectDashboards).pipe(filter(isNotNull))),
      concatMap(([{ dashboardId, blocks }, dashboards]) => {
        const dashboard = dashboards.find((d) => d.id === dashboardId);
        if (dashboard) {
          return of(DashboardActions.updateDashboard({ dashboard: { ...dashboard, blocks } }));
        }
        return throwError(() => new Error('Could not find dashboard in the store'));
      })
    );
  });

  deleteDashboardBlock$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.deleteDashboardBlock),
      concatLatestFrom(() => this.store.select(selectDashboards).pipe(filter(isNotNull))),

      concatMap(([{ dashboardId, blockId }, dashboards]) => {
        const dashboard = dashboards.find((d) => d.id === dashboardId);
        if (dashboard) {
          return of(
            DashboardActions.updateDashboard({
              dashboard: { ...dashboard, blocks: dashboard.blocks.filter((b) => b.id !== blockId) },
            })
          );
        }
        return throwError(() => new Error('Could not find dashboard in the store'));
      })
    );
  });

  updateDashboardBlock$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.updateDashboardBlock),
      concatLatestFrom(() => this.store.select(selectDashboards).pipe(filter(isNotNull))),
      concatMap(([{ dashboardId, block }, dashboards]) => {
        const dashboard = dashboards.find((d) => d.id === dashboardId);
        if (dashboard) {
          return of(
            DashboardActions.updateDashboard({
              dashboard: {
                ...dashboard,
                blocks: dashboard.blocks.map((b) => (b.id === block.id ? block : b)),
              },
            })
          );
        }
        return throwError(() => new Error('Could not find dashboard in the store'));
      })
    );
  });
  /*
  createDashboardBlock$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.createDashboardBlock),
      concatLatestFrom(() => this.store.select(selectDashboards).pipe(filter(isNotNull))),
      concatMap(([{ dashboardId, block }, dashboards]) => {
        let dashboard = dashboards.find((d) => d.id === dashboardId);
        if (dashboard) {
          if (!dashboard.blocks) {
            dashboard = { ...dashboard, blocks: [] };
          }
          return of(
            DashboardActions.updateDashboard({
              dashboard: {
                ...dashboard,
                blocks: [...dashboard.blocks, { ...block, id: 'new', created_at: new Date() }],
              },
            })
          );
        }
        return throwError(() => new Error('Could not find dashboard in the store'));
      })
    );
  });
  */
  httpResult$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        DashboardActions.loadDashboardsSuccess,
        DashboardActions.loadDashboardsFailure,
        DashboardActions.createDashboardSuccess,
        DashboardActions.createDashboardFailure,
        DashboardActions.updateDashboardSuccess,
        DashboardActions.updateDashboardFailure,
        DashboardActions.deleteDashboardSuccess,
        DashboardActions.deleteDashboardFailure,
        DashboardActions.loadDashboardsFailure
      ),
      map((res) => DashboardActions.httpResult({ action: res, error: res }))
    );
  });
}
